import styled, { css, keyframes } from 'styled-components';
import { GEM_SHAPE } from './gemShape';

export const modalName = 'modal';
export const modalTimeout = 200;

/** Width over height of a printed card. */
export const CARD_ASPECT = 600 / 825;

/**
 * How round a card's corners are, against its width: a real card's, measured off the photo of a card back
 * (public/image/pokemon-card.jpg, a 19px radius on a 371px card). That photo's corners are filled in with its border
 * blue, so only this rounding shapes them.
 */
export const CARD_CORNER = 0.051;

/** Every card on screen takes its width from --card-w, which the layout around it sets. */
const cardSize = css`
  width: var(--card-w, 64px);
  aspect-ratio: ${CARD_ASPECT};
  border-radius: calc(var(--card-w, 64px) * ${CARD_CORNER});
  flex: none;
`;

/** Badges you read stay upright when the card is turned for a Special Condition (CardFace sets --upright). */
const upright = css`
  transform: var(--upright, none);
  transition: transform .35s ease;
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 2px var(--accent), 0 0 6px 1px rgba(255, 203, 5, .5); }
  50% { box-shadow: 0 0 0 2px var(--accent), 0 0 14px 4px rgba(255, 203, 5, .7); }
`;

/**
 * A spot that can be picked, or dropped on: it pulses. Once one is tapped and its question is open (ConfirmButton's
 * data-chosen), that one is lifted with a solid glow and the others stop pulsing, so it's clear which is being asked
 * about.
 */
export const targetable = css`
  animation: ${pulse} 1.4s ease-in-out infinite;
  transition: transform .15s ease-out;

  body:has([data-chosen]) & {
    animation: none;
    box-shadow: 0 0 0 1px rgba(255, 203, 5, .45);
  }

  body:has([data-chosen]) [data-chosen] & {
    box-shadow: 0 0 0 3px var(--accent), 0 0 16px 6px rgba(255, 203, 5, .8);
    transform: translateY(-4px) scale(1.06);
  }
`;

export const Face = styled.div`
  ${cardSize}
  position: relative;
  transition: transform .35s ease;

  ${props => props.$targetable && targetable}
  ${props => props.$dim && css`filter: saturate(.15) brightness(.6);`}
  ${props => props.$selected && css`box-shadow: 0 0 0 3px var(--accent);`}
`;

export const Slot = styled.div`
  ${cardSize}
  border: 1.5px dashed var(--line);
  display: grid;
  place-items: center;
  color: var(--accent);
  font-size: calc(var(--card-w, 64px) * .4);
  font-weight: 300;

  ${props => props.$targetable && css`
    border-color: var(--accent);
    background: rgba(255, 203, 5, .08);
    ${targetable}
  `}
`;

/** Badges keep a readable size on small cards, and grow with big ones. */
const badgeSize = 'clamp(16px, calc(var(--card-w, 64px) * .3), 28px)';

/** A Pokémon's damage counters (gemLayout), laid over the card. */
export const Gems = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const dropIn = keyframes`
  from { transform: translate(-50%, -50%) translateY(-30%) scale(1.25); opacity: 0; }
  to { transform: translate(-50%, -50%); opacity: 1; }
`;

/**
 * One glass damage counter, like the flat-backed glass gems the game came with: the card shows through it, with a
 * dark rim, a bright highlight, light gathering on its far side and a shadow on the card. It drops in as it's added.
 */
export const Gem = styled.span`
  position: absolute;
  width: calc(var(--card-w, 64px) * ${props => props.$size});
  aspect-ratio: ${GEM_SHAPE};
  border-radius: 50%;
  transform: translate(-50%, -50%);
  animation: ${dropIn} .28s ease-out;
  background: var(--glass);
  box-shadow: 1px 3px 3px -1px rgba(0, 0, 0, .45), inset 0 0 0 1px var(--rim), inset -2px -3px 5px rgba(0, 0, 0, .28),
    inset 1px 2px 3px rgba(255, 255, 255, .4);

  ${props => (props.$color === 'blue' ? css`
    --glass: radial-gradient(circle at 60% 62%, rgba(70, 140, 220, .82) 0%, rgba(18, 70, 160, .84) 45%, rgba(6, 28, 82, .94) 100%);
    --rim: rgba(4, 18, 60, .85);
    --glow: rgba(140, 195, 255, .7);
  ` : css`
    --glass: radial-gradient(circle at 60% 62%, rgba(220, 130, 40, .84) 0%, rgba(150, 70, 10, .86) 45%, rgba(78, 32, 4, .94) 100%);
    --rim: rgba(52, 20, 2, .85);
    --glow: rgba(255, 190, 110, .7);
  `)}

  &::before {
    content: '';
    position: absolute;
    left: 20%;
    top: 10%;
    width: 34%;
    height: 20%;
    border-radius: 50%;
    background: radial-gradient(ellipse at 45% 45%, #fff 0%, rgba(255, 255, 255, .85) 35%, rgba(255, 255, 255, 0) 72%);
    transform: rotate(-24deg);
  }

  &::after {
    content: '';
    position: absolute;
    right: 14%;
    bottom: 12%;
    width: 40%;
    height: 28%;
    border-radius: 50%;
    background: radial-gradient(ellipse, var(--glow), rgba(255, 255, 255, 0) 72%);
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/** Special conditions, protection from attacks and attached Trainers, stacked across the card. */
export const StatusBadges = styled.ul`
  position: absolute;
  top: 18%;
  left: 0;
  right: 0;
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  justify-items: center;
  gap: 2px;
  ${upright}

  li {
    padding: 1px 6px;
    border-radius: 999px;
    background: rgba(90, 40, 140, .9);
    color: #fff;
    font-size: clamp(9px, calc(var(--card-w, 64px) * .17), 13px);
    font-weight: 700;
    box-shadow: 0 0 0 1.5px rgba(0, 0, 0, .5);
  }

  li[data-kind='protection'] {
    background: rgba(20, 90, 170, .92);
  }

  li[data-kind='hindrance'],
  li[data-kind='conversion'] {
    background: rgba(160, 90, 20, .92);
  }

  li[data-kind='trainer'] {
    background: rgba(30, 120, 60, .92);
  }
`;

/** How big the attached energy symbols are on a card. */
const energySize = 'clamp(9px, calc(var(--card-w, 64px) * .17), 18px)';

export const Energies = styled.div`
  position: absolute;
  --energy-size: ${energySize};
  left: 6%;
  bottom: calc(${energySize} * -.4);
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  max-width: 90%;
`;

export const Count = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 6%;
  width: fit-content;
  margin: 0 auto;
  padding: 1px 7px;
  border-radius: 999px;
  background: rgba(0, 0, 0, .75);
  color: var(--ink);
  font-size: clamp(10px, calc(var(--card-w, 64px) * .22), 14px);
  font-weight: 700;
  white-space: nowrap;
`;

export const Modal = styled.div`
  position: fixed;
  inset: 0;
  z-index: 40;
  display: grid;
  place-items: center;
  padding: 24px;
  background: rgba(0, 0, 0, .7);
  opacity: 0;
  transition: opacity ease ${modalTimeout}ms;
  pointer-events: none;
  --card-w: min(80vw, 60dvh * ${CARD_ASPECT}, 420px);

  &.${modalName}-enter-active, &.${modalName}-enter-done {
    opacity: 1;
    pointer-events: auto;
  }
`;

/**
 * The card's picture, the whole image edge to edge: the card back's photo has its corners rounded already, and they
 * line up with the card's own only if none of it is cropped.
 */
export const Art = styled.div`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: #2b2b2b center / 100% 100% no-repeat;
  box-shadow: 0 1px 3px rgba(0, 0, 0, .55);
`;


/** A Special Condition's marker on a Pokémon (Card/Markers): the token's picture at its top left, like a real one. */
export const Marker = styled.span`
  /*
   * The real token's size against the card's width: the official markers are 1.2 in (30.5 mm) across, the card
   * 63 mm wide, so 48%. Never too small to make out.
   */
  --marker-size: max(18px, calc(var(--card-w, 64px) * .48));
  position: absolute;
  top: 6%;
  left: -6%;
  width: var(--marker-size);
  aspect-ratio: 1;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, .6);
  ${upright}

  & > img {
    display: block;
    width: 100%;
    height: 100%;
  }

  & + & {
    top: calc(6% + var(--marker-size) + 3px);
  }
`;

/**
 * Attached Trainers' icons (Card/Markers), stacked up the card's right edge from near the bottom, clear of the energy
 * symbols along the bottom.
 */
export const Trainers = styled.div`
  position: absolute;
  --energy-size: ${energySize};
  --icon-h: calc(var(--energy-size) * 1.25);
  right: calc(${energySize} * -.6);
  bottom: 14%;
  display: flex;
  flex-direction: column-reverse;
  align-items: flex-end;

  /*
   * A 2px gap while they fit; with more (up to four PlusPowers and four Defenders), each overlaps the one below just
   * enough for the stack to stay within about 80% of the card's height (--trainers is how many).
   */
  & > * + * {
    margin-bottom: min(2px, calc((var(--card-w, 64px) * ${0.8 / CARD_ASPECT} - var(--trainers) * var(--icon-h)) / (var(--trainers) - 1)));
  }
`;

/**
 * A Trainer card's artwork, zoomed into a circle a little bigger than an energy symbol: twice the card's width,
 * centred 40% of the way down the card, where the attachable Trainers draw their item (PlusPower, Defender).
 */
export const TrainerIcon = styled.span`
  width: calc(var(--energy-size) * 1.3);
  aspect-ratio: 1;
  border-radius: 50%;
  background: #222 no-repeat 50% 34% / 200%;
  box-shadow: 0 0 0 1.5px #fff, 0 1px 3px rgba(0, 0, 0, .6);
`;

/** An attached Trainer's item cut out of its artwork (Card/Markers), as tall as an energy symbol and a bit. */
export const TrainerCutOut = styled.img`
  height: calc(var(--energy-size) * 1.25);
  width: auto;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, .7));
`;
